import * as SVG from 'svg.js';

import { GalleryImage } from 'app/gallery/gallery.service';
import { GalleryConstants } from 'core/constants';
import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';

interface Dimensions
{
  targetWidth: number;
  targetHeight: number;
}

interface ImageEnvironmentObjects
{
  img: SVG.Image;
  wrapper: SVG.Element;
  clipper: SVG.ClipPath;
}

function getImageEnvironment(img: SVG.Image): ImageEnvironmentObjects
{
  const wrapper = (<any>img).parent() as SVG.Element;

  const findClipPath = (clipped: SVG.Element): SVG.ClipPath => {
    const pattern = /^url\(\"(.+)\"\)$/;
    const clipAttr = clipped.attr('clip-path');
    const clipPathMatch = clipAttr && clipAttr.match(pattern);
    return clipPathMatch && (<any>img).doc().select(clipPathMatch[1]).first();
  };

  return {
    img: img,
    wrapper: wrapper,
    clipper: wrapper && findClipPath(wrapper),
  };
}

function determineTargetDimensions(
  img: SVG.Image, galleryImage?: GalleryImage): Dimensions
{
  const DEFAULT_DIMENSIONS = <Dimensions> {
    targetWidth: GalleryConstants.THUMB_HEIGHT,
    targetHeight: GalleryConstants.THUMB_HEIGHT,
  };

  const toDimensions = (box: SVG.Box) => <Dimensions> {
    targetWidth: box.width,
    targetHeight: box.height };

  const isValidBox = (box: SVG.Box): boolean =>
    box.width !== 0 && box.height !== 0;

  const withoutUndefined = (els: Array<any>): Array<any> =>
    els.filter(el => !!el);

  const correctAspectRatio = (dim: Dimensions, aspect: number): Dimensions => (
    aspect < 1
      ? { targetWidth: dim.targetWidth,
          targetHeight: dim.targetWidth / aspect}
      : { targetWidth: dim.targetHeight * aspect,
          targetHeight: dim.targetHeight});

  const tryCorrectAspectRatio = (dim: Dimensions): Dimensions => (
    galleryImage
      ? correctAspectRatio(dim, galleryImage.width / galleryImage.height)
      : dim);

  const rounded = (dim: Dimensions): Dimensions => <Dimensions> {
    targetWidth: Math.round(dim.targetWidth),
    targetHeight: Math.round(dim.targetHeight) };

  const env = getImageEnvironment(img);

  const elements = galleryImage
    ? [env.clipper, env.wrapper, env.img]
    : [env.img, env.clipper, env.wrapper];

  const boxDims = withoutUndefined(elements)
    .map(el => el as SVG.Element)
    .map(el => el.rbox())
    .filter(isValidBox)
    .map(toDimensions)
    .concat([DEFAULT_DIMENSIONS])
    .map(tryCorrectAspectRatio)
    .map(rounded);

  return withoutUndefined(boxDims).shift();
}

function determineBaseUrl(url: string): string
{
  return url.split('?')[0];
}

function determineMultiversionUrl(
  img: SVG.Image, baseUrl: string, galleryImage?: GalleryImage): string
{
  const dimensions = determineTargetDimensions(img, galleryImage);

  return baseUrl + '?' +
      `limit={"width": ${Utils.steppedLimit(dimensions.targetWidth)}, ` +
      `"height": ${Utils.steppedLimit(dimensions.targetHeight)}}`;
}

function multiversionImageLoad(
  url?: string, galleryImage?: GalleryImage): string | undefined
{
  const requestedUrl =
      url || this.node.getAttributeNS('http://www.w3.org/1999/xlink', 'href');

  if (! requestedUrl || requestedUrl === '') {
    return url;
  }

  const baseUrl = determineBaseUrl(requestedUrl);

  this.attr('pap:href', baseUrl);

  const finalUrl = (
      StageUtils.isPlaceholder(this.node) ||
          StageUtils.isAsset(this.node.parentNode)
          ? url
          : determineMultiversionUrl(this, baseUrl, galleryImage));

  return SVG.Image.prototype.load.bind(this)(finalUrl);
}

function delayedFunction(fn: () => any, timeout: number): () => any
{
  let timeoutHandle = undefined;

  return () => {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }

    timeoutHandle = setTimeout(() => fn(), timeout);
  };
}

function attachResizeListener(img: SVG.Image, timeout: number): SVG.Image
{
  const resizeHandler = delayedFunction(() => {
    if (! document.body.contains(img.node)) {
      window.removeEventListener('resize', resizeHandler);
      return;
    }

    img.load();
  }, timeout);

  window.addEventListener('resize', resizeHandler);

  return img;
}

export function loadGalleryImage(
  img: SVG.Image, galleryImage: GalleryImage): string
{
  return multiversionImageLoad.bind(img)(galleryImage.url, galleryImage);
}

export function injectMultiversionImageBehavior(img: any): SVG.Image
{
  const RELOAD_TIMEOUT = 800;

  img.load = multiversionImageLoad.bind(img);
  img.scheduleReload = delayedFunction(img.load, RELOAD_TIMEOUT);
  attachResizeListener(img, RELOAD_TIMEOUT);

  return img as SVG.Image;
}

export function findMultiversionImageCandidates(svgDoc: any): any
{
  return StageUtils.getAllWrappedImages(SVG(<HTMLElement> svgDoc.firstChild))
    .map((wrapped) => wrapped.image.node);
}



// WEBPACK FOOTER //
// ./src/app/controls/multiversion-image/multiversion-image.ts